import { useQuery } from '@apollo/client'
import {
    Box,
    Link,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import ErrorHandler from 'react-common-eks/dist/components/ErrorHandler'
import Hero from 'react-common-eks/dist/components/Hero'
import ImgIX from 'react-common-eks/dist/components/ImgIX'
import Paragraph from 'react-common-eks/dist/components/Paragraph'
import RicePaper from 'react-common-eks/dist/components/RicePaper'
import formatPhoneNumber, {
    PHONE_NUMBER_FORMATS,
} from 'react-common-eks/dist/data/formaters/format-phone-number'
import useBreadcrumbs from 'react-common-eks/dist/hooks/use-breadcrumbs'
import { Helmet } from 'react-helmet-async'

import { GET_SHIHAN_CONTACT_INFO } from './queries'

const SHIHAN_EMAIL_KEY = 'eks.shihan.email'
const SHIHAN_PHONE_KEY = 'eks.shihan.phone'

const Home = () => {
    const theme = useTheme()
    const mdUp = useMediaQuery(theme => theme.breakpoints.up('md'))

    useBreadcrumbs([])

    const [email, setEmail] = useState(localStorage.getItem(SHIHAN_EMAIL_KEY))
    const [phone, setPhone] = useState(localStorage.getItem(SHIHAN_PHONE_KEY))

    const { data, loading, error } = useQuery(GET_SHIHAN_CONTACT_INFO)

    useEffect(() => {
        if (data) {
            const foundEmail = _.get(data, 'shihan.email')
            if (!_.isNil(foundEmail)) {
                setEmail(foundEmail)
                localStorage.setItem(SHIHAN_EMAIL_KEY, foundEmail)
            }
            const foundPhone = _.get(data, 'shihan.phone')
            if (!_.isNil(foundPhone)) {
                setPhone(foundPhone)
                localStorage.setItem(SHIHAN_PHONE_KEY, foundPhone)
            }
        }
    }, [data])

    if (error) return <ErrorHandler error={error} />

    const imgMaxWidth = 0.33 * theme.breakpoints.values.sm
    const imgParams = {
        fit: 'crop',
        w: imgMaxWidth,
    }
    const hero = (
        <Hero
            sx={{
                maxWidth: {
                    md: '50%',
                },
            }}
        >
            <Typography paragraph>
                Bernard Edwards, Branch Chief and President of Ryukyu Kobujutsu
                Hozon Shinko Kai USA, is a member of Ryukyu Kobujutsu Hozon
                Shinko Kai General Headquarters in Tokyo, Japan. Sensei Edwards
                is under the direction of Hanshi Kisho Inoue.
            </Typography>
        </Hero>
    )
    return (
        <Paragraph
            sx={{ mt: { sm: 4 } }}
            component="article"
            id="organization-rkhskusa"
            itemScope
            itemType="https://schema.org/Organization"
        >
            <Helmet>
                <title>Welcome to Ryukyu Kobujutsu Hozon Shinko Kai USA</title>
            </Helmet>

            <Stack spacing={2}>
                <Box>
                    <Paper sx={{ p: 4 }}>
                        <Stack
                            direction="row"
                            spacing={1}
                            gridTemplateColumns="1fr auto 1fr"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Box
                                sx={{ float: 'left' }}
                                marginRight={1}
                                marginBottom={1}
                            >
                                <ImgIX
                                    itemProp="image"
                                    src="/images/edwards_portrait.jpg"
                                    width="100%"
                                    params={imgParams}
                                />
                            </Box>

                            {mdUp && hero}

                            <Box
                                sx={{ float: 'right' }}
                                marginRight={1}
                                marginBottom={1}
                            >
                                <ImgIX
                                    itemProp="image"
                                    src="/images/rkhsk_logo_180X180.png"
                                    width="100%"
                                    alt="Ryukyu Kobujutsu Hozon Shinko Kai USA logo"
                                    params={{
                                        ...imgParams,
                                        mask: 'ellipse',
                                        h: imgParams.w,
                                        'fp-z': 1.1,
                                    }}
                                />
                            </Box>
                        </Stack>

                        {!mdUp && hero}

                        <Typography paragraph>
                            Ryukyu Kobujutsu Hozon Shinko Kai USA is affiliated
                            with Edwards Karate School and operates as a
                            separate club or group. Sensei Edwards is also the
                            President and Chief Master Instructor of{' '}
                            <Link href="https://www.hakuakai.com/">
                                Hakua Kai Karate-Do USA and North America
                            </Link>
                            .
                        </Typography>
                        <Typography paragraph>
                            Ryukyu Kobujutsu Hozon Shinko Kai USA offers
                            opportunities to study kobujutsu (kobudo). Don't
                            hesitate to{' '}
                            <Link href="/contact-us">contact us</Link> if you
                            operate a school, club, or organization and would
                            like to host a Ryukyu Kobujutsu course/seminar or if
                            you want information about our classes.
                        </Typography>
                        <Typography paragraph>
                            Those who train with us have the opportunity to
                            become members of Ryukyu Kobujutsu Hozon Shinko Kai
                            USA.
                        </Typography>
                        <Typography>
                            Any person or organization who does not belong to
                            the Ryukyu Kobujutsu Hozon Shinko Kai (RKHSK) USA is
                            not permitted to wear the patch so designated on
                            this web site unless authorized by the RKHSK USA
                            Headquarters.
                        </Typography>
                    </Paper>
                </Box>
                <RicePaper>
                    <Typography variant="h2" gutterBottom>
                        Honors
                    </Typography>
                    <Typography paragraph>
                        For his contribution to the traditional Japanese martial
                        arts, Shihan Edwards was inducted into the{' '}
                        <Link href="http://www.mastershalloffame.org/?q=content/mhof-inductee-list-2010">
                            <span itemProp="award">
                                Martial Arts Masters Hall of Fame
                            </span>{' '}
                            in 2010
                        </Link>{' '}
                        and the{' '}
                        <Link href="http://www.nichibeikai.org/page2/index.html">
                            <span itemProp="award">
                                Hokka Nichibeikai Bunka Hall of Fame
                            </span>{' '}
                            in 2016
                        </Link>
                        . The Martial Arts Masters Hall of Fame recognizes
                        leaders of the martial arts community. The Bunka Hall of
                        Fame's purpose is to honor those who have excelled and
                        contributed to the culture of and bettered relationships
                        in our community by enlightening all to the culture of
                        Japan as practiced in America.
                    </Typography>
                </RicePaper>
                <Paper sx={{ p: 4 }}>
                    <Typography variant="h2" gutterBottom>
                        Classes
                    </Typography>
                    <Typography paragraph>
                        Regular classes are held at Foster City Recreation
                        Center and a two-hour session once a month.
                    </Typography>
                    <Typography aria-busy={loading}>
                        Please contact Shihan Edwards for specific details by{' '}
                        {email && <Link href={`mailto:${email}`}>email</Link>}
                        {!email && 'email'} or by phone
                        {phone && (
                            <>
                                {' at '}
                                {formatPhoneNumber(
                                    phone,
                                    PHONE_NUMBER_FORMATS.NATIONAL
                                )}
                            </>
                        )}
                        .
                    </Typography>
                </Paper>
            </Stack>
        </Paragraph>
    )
}

export default Home
